import { add } from 'date-fns';
import { Cookies } from 'react-cookie';
import { isEmbeddedInIframe } from './tenant-origin';
import { InputTokens } from './types';

const cookies = new Cookies();

type TokenKey = 'accessToken' | 'refreshToken' | 'idToken';

const memoryTokens: Partial<Record<TokenKey, string>> = {};
const IFRAME_STORAGE_PREFIX = 'tv.embed.';

const cookieDomain = () => {
  const parsedHostName = window.location.hostname.split('.');
  return parsedHostName.length > 2
    ? `.${parsedHostName.slice(1).join('.')}`
    : parsedHostName.join('.');
};

const readSessionToken = (key: TokenKey): string | undefined => {
  try {
    return (
      sessionStorage.getItem(`${IFRAME_STORAGE_PREFIX}${key}`) ?? undefined
    );
  } catch {
    return undefined;
  }
};

const writeSessionToken = (key: TokenKey, value: string) => {
  try {
    if (!value) {
      sessionStorage.removeItem(`${IFRAME_STORAGE_PREFIX}${key}`);
      return;
    }
    sessionStorage.setItem(`${IFRAME_STORAGE_PREFIX}${key}`, value);
  } catch {
    // sessionStorage can be blocked in some embed / private contexts
  }
};

const clearSessionToken = (key: TokenKey) => {
  try {
    sessionStorage.removeItem(`${IFRAME_STORAGE_PREFIX}${key}`);
  } catch {
    // ignore unavailable sessionStorage
  }
};

export const setToken = (key: string, value?: string) => {
  const tokenKey = key as TokenKey;
  const token = value ?? '';
  memoryTokens[tokenKey] = token;

  // Always attempt cookies (same as before this fix). In a third-party iframe
  // Safari/Firefox will drop them; Chrome often keeps them. Skipping the write
  // would break iframe login that later jumps to window.top.
  cookies.set(key, token, {
    expires: add(new Date(), { days: 30 }),
    sameSite: 'none',
    domain: cookieDomain(),
    secure: true,
    path: '/',
  });

  if (isEmbeddedInIframe()) {
    writeSessionToken(tokenKey, token);
  }
};

export const clearToken: (key: string) => void = (key) => {
  const tokenKey = key as TokenKey;
  delete memoryTokens[tokenKey];
  clearSessionToken(tokenKey);
  cookies.remove(key, { domain: cookieDomain(), path: '/' });
};

export const saveAuth = (data: InputTokens) => {
  setToken('accessToken', data.accessToken);
  setToken('refreshToken', data.refreshToken);
  setToken('idToken', data.idToken);
};

export const clearAuth = () => {
  clearToken('accessToken');
  clearToken('refreshToken');
  clearToken('idToken');
};

const getToken = (key: TokenKey): string => {
  const fromMemory = memoryTokens[key];
  if (fromMemory) {
    return fromMemory;
  }

  if (isEmbeddedInIframe()) {
    const fromSession = readSessionToken(key);
    if (fromSession) {
      memoryTokens[key] = fromSession;
      return fromSession;
    }
  }

  return cookies.get<string>(key) || '';
};

export const getAccessToken = () => getToken('accessToken');

export const getRefreshToken = () => getToken('refreshToken');

export const getIdToken = () => getToken('idToken');

export const hasAuth = () => !!getIdToken();
